import { activities as activitiesData, projects } from "../../../../../db";
import {
  calculateDurationInMonths,
  calculateProfessionalAttributedProjectDuration,
  normalizeSkillFormat,
  createSkillMaps,
  addSkillToMap,
  normalizeSkillMaps,
  type SkillMaps,
  type NormalizedSkillsByCategory
} from './skillCalculationUtils';

interface ProfessionalSkillsByCategory {
  profession: Array<{ name: string; level: number; }>;
  lead: Array<{ name: string; level: number; }>;
  language: Array<{ name: string; level: number; }>;
  tools: Array<{ name: string; level: number; }>;
}

// Memoization cache for performance
const memoizedProfessionalSkillsCache = new Map<string, ProfessionalSkillsByCategory>();

/**
 * Calculate overall professional skills across ALL activities and projects
 * Uses normalized calculation against total project duration
 */
export function calculateProfessionalSkills(): ProfessionalSkillsByCategory {
  // Simple cache key based on projects data length and activities length
  const cacheKey = `${projects.length}-${activitiesData.length}`;
  
  if (memoizedProfessionalSkillsCache.has(cacheKey)) {
    return memoizedProfessionalSkillsCache.get(cacheKey)!;
  }

  const skillMaps = createSkillMaps();

  // Get only professional projects (exclude private projects with activityId === null)
  // Professional overview should only include work-related projects
  const professionalProjects = projects.filter((project: any) => project.activityId !== null);

  // Calculate total duration of professional projects for normalization
  const totalProfessionalProjectMonths = professionalProjects.reduce((total, project) => {
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    return total + attributedDuration;
  }, 0);

  // Process only professional projects (work-related projects)
  professionalProjects.forEach((project: any) => {
    // Handle multi-activity projects by splitting duration equally for professional overview
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    
    // Get normalized skill format (handles both legacy and new formats)
    const normalizedSkills = normalizeSkillFormat(project);
    
    // Process all skill categories
    Object.entries(normalizedSkills).forEach(([category, skills]) => {
      const categoryKey = category as keyof SkillMaps;
      skills.forEach((skill: any) => {
        if (!skill.name || typeof skill.weight !== 'number') return;
        
        addSkillToMap(skillMaps, categoryKey, skill.name, attributedDuration, skill.weight);
      });
    });
  });

  // Calculate normalized skill levels (0-100) for each category
  // Important: Normalize against ALL professional project duration, not just projects where skill appears
  const skillsByCategory = normalizeSkillMaps(skillMaps, totalProfessionalProjectMonths);

  memoizedProfessionalSkillsCache.set(cacheKey, skillsByCategory);
  return skillsByCategory;
}

/**
 * Get total skills count across all professional activities
 */
export function getTotalProfessionalSkillsCount(): number {
  const skills = calculateProfessionalSkills();
  return Object.values(skills).reduce((total, categorySkills) => total + categorySkills.length, 0);
}
